// src/pages/onboarding/Onboarding.jsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Loader2, Pencil, Trash2, Search, UserCheck,
  ChevronDown, ChevronUp, X, Check, AlertCircle,
  Camera, Upload, Trash, CheckCircle
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { employeeService } from '../../services/employeeService';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import StatusBadge from '../../components/StatusBadge';

const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Contract'];
const ROLES = [
  { value: 'employee', label: 'Employee' },
  { value: 'hr_manager', label: 'HR Manager' },
  { value: 'finance_manager', label: 'Finance Manager' },
  { value: 'sales_manager', label: 'Sales Manager' },
  { value: 'sales_rep', label: 'Sales Representative' },
];

const EMPTY_FORM = {
  employee_id: '',
  full_name: '',
  email: '',
  phone: '',
  cnic: '',
  date_of_birth: '',
  emergency_contact_name: '',
  emergency_contact_phone: '',
  father_name: '',
  address: '',
  bank_name: '',
  bank_account_number: '',
  department: '',
  designation: '',
  employment_type: 'Full-time',
  role: 'employee',
  joining_date: new Date().toISOString().slice(0, 10),
  status: 'active',
  personal_email: '',
  reporting_manager: '',
  basic_salary: null,
  transport_allowance: null,
  housing_allowance: null,
  medical_allowance: null,
  other_allowance: null,
  tax_deduction: null,
  loan_repayment: null,
  other_deduction: null,
};

// Draft persistence for the "New Employee" form — keeps half-filled data
// safe if the user navigates to another page mid-fill and comes back,
// without touching any existing create/edit/submit logic.
const DRAFT_STORAGE_KEY = 'onboarding_new_employee_draft';

const loadOnboardingDraft = () => {
  try {
    const raw = sessionStorage.getItem(DRAFT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
};

const saveOnboardingDraft = (draft) => {
  try {
    sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch (err) {
    // sessionStorage full/unavailable (e.g. private browsing) — fail silently, no crash
  }
};

const clearOnboardingDraft = () => {
  try {
    sessionStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch (err) {
    // ignore
  }
};

const inputCls = 'w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light';


const Field = ({ label, required, children, helperText }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">
      {label}{required && <span className="text-red-500"> *</span>}
    </label>
    {children}
    {helperText && <p className="text-xs text-gray-400 mt-1">{helperText}</p>}
  </div>
);

const Section = ({ id, title, open, onToggle, children, badge }) => (
  <div className="border border-gray-100 rounded-card overflow-hidden mb-4">
    <button
      type="button"
      onClick={() => onToggle(id)}
      className="w-full flex items-center justify-between px-5 py-3.5 bg-gray-50 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
    >
      <span>{title}</span>
      <span className="flex items-center gap-2">
        {badge}
        {open
          ? <ChevronUp className="w-4 h-4 text-gray-400" />
          : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </span>
    </button>
    {open && (
      <div className="p-5 bg-white grid grid-cols-1 sm:grid-cols-2 gap-4">
        {children}
      </div>
    )}
  </div>
);

const Onboarding = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const isEditMode = Boolean(employeeId);

  const canAccess = user && (user.role === 'super_admin' || user.role === 'hr_manager');

  const [employees, setEmployees] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [departments, setDepartments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  useEffect(() => {
    if (formSuccess) {
      const t = setTimeout(() => setFormSuccess(''), 2000);
      return () => clearTimeout(t);
    }
  }, [formSuccess]);
  const [loadingForm, setLoadingForm] = useState(false);

  const [facePhotos, setFacePhotos] = useState([]);
  const [cameraModeActive, setCameraModeActive] = useState(false);
  const [facePhotoError, setFacePhotoError] = useState('');
  const [enrollingFace, setEnrollingFace] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [openSections, setOpenSections] = useState({
    auth: true,
    personal: true,
    job: true,
    salary: true,
    face_enrollment: false,
  });

  const toggleSection = useCallback(
    (key) => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] })),
    []
  );


  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handlePhoneInput = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 15) setField('phone', value);
  };

  const handleCNICInput = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 20) setField('cnic', value);
  };

  const handleEmergencyPhoneInput = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 15) setField('emergency_contact_phone', value);
  };

  const loadEmployees = useCallback(async () => {
    setListLoading(true);
    setListError('');
    try {
      const res = await employeeService.getEmployees(undefined, undefined, search || undefined);
      setEmployees(res.data || []);
    } catch (err) {
      setListError(err.message || 'Failed to load employees');
    } finally {
      setListLoading(false);
    }
  }, [search]);

  const loadDepartments = async () => {
    try {
      const res = await employeeService.getDepartments();
      let deptArray = [];
      if (res && res.data && Array.isArray(res.data)) {
        deptArray = res.data;
      } else if (Array.isArray(res)) {
        deptArray = res;
      } else if (res && res.departments && Array.isArray(res.departments)) {
        deptArray = res.departments;
      }
      setDepartments(deptArray);
    } catch (err) {
      console.error('Department loading error:', err);
      setDepartments([]);
    }
  };

  useEffect(() => {
    loadEmployees();
    loadDepartments();
  }, []);

  // Restore an in-progress "New Employee" draft on mount, if one exists.
  // Only applies to the create flow — edit mode always loads fresh data
  // from the server via its own effect below.
  useEffect(() => {
    if (isEditMode) return;
    const draft = loadOnboardingDraft();
    if (draft && draft.showForm) {
      setForm(draft.form || EMPTY_FORM);
      setFacePhotos(draft.facePhotos || []);
      if (draft.openSections) setOpenSections(draft.openSections);
      setShowForm(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Continuously persist the "New Employee" form as the user types, so
  // navigating away and back restores it. Auto-clears once the form is
  // closed (Cancel, or after a successful submit closes it via closeForm).
  useEffect(() => {
    if (isEditMode) return;
    if (!showForm) {
      clearOnboardingDraft();
      return;
    }
    saveOnboardingDraft({ form, facePhotos, openSections, showForm });
  }, [form, facePhotos, openSections, showForm, isEditMode]);

  useEffect(() => {
    if (!isEditMode) return;

    const fetchEmployee = async () => {
      setLoadingForm(true);
      setFormError('');
      setFormSuccess('');
      try {
        const res = await employeeService.getEmployee(employeeId);
        const e = res.data || res;

        // Load existing salary structure (if any) so edit form shows real values
        let salaryDefaults = {
          basic_salary: null,
          transport_allowance: null,
          housing_allowance: null,
          medical_allowance: null,
          other_allowance: null,
          tax_deduction: null,
          loan_repayment: null,
          other_deduction: null,
        };
        try {
          const salaryRes = await api.get('/api/payroll/salary-structure/' + employeeId);
          const s = salaryRes.data || salaryRes;
          if (s) {
            salaryDefaults = {
              basic_salary: s.basic_salary ?? null,
              transport_allowance: s.allowances?.transport ?? null,
              housing_allowance: s.allowances?.housing ?? null,
              medical_allowance: s.allowances?.medical ?? null,
              other_allowance: s.allowances?.other ?? null,
              tax_deduction: s.deductions?.tax ?? null,
              loan_repayment: s.deductions?.loan_repayment ?? null,
              other_deduction: s.deductions?.other ?? null,
            };
          }
        } catch (salaryLoadErr) {
          // No salary structure yet for this employee - defaults (null) stay.
          console.warn('No existing salary structure found:', salaryLoadErr);
        }

        setForm({
          employee_id: e.employee_id || '',
          full_name: e.full_name || '',
          email: e.email || '',
          phone: e.phone != null ? String(e.phone) : '',
          cnic: e.cnic || '',
          date_of_birth: e.date_of_birth ? e.date_of_birth.slice(0, 10) : '',
          emergency_contact_name: e.emergency_contact_name || '',
          emergency_contact_phone: e.emergency_contact_phone != null ? String(e.emergency_contact_phone) : '',
          father_name: e.father_name || '',
          address: e.address || '',
          bank_name: e.bank_name || '',
          bank_account_number: e.bank_account_number || '',
          department: e.department || '',
          designation: e.designation || '',
          employment_type: e.employment_type || 'Full-time',
          role: e.role || 'employee',
          joining_date: e.joining_date ? e.joining_date.slice(0, 10) : '',
          status: e.status || 'active',
          personal_email: e.personal_email || '',
          reporting_manager: e.reporting_manager || '',
          ...salaryDefaults,
        });

        if (e.face_images && Array.isArray(e.face_images)) {
          setFacePhotos(e.face_images);
        } else {
          setFacePhotos([]);
        }

        setShowForm(true);
      } catch (err) {
        setFormError(err.message || 'Failed to load employee');
        setShowForm(true);
      } finally {
        setLoadingForm(false);
      }
    };

    fetchEmployee();
  }, [employeeId, isEditMode]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 480 }, height: { ideal: 480 } },
      });
      streamRef.current = stream;
      setOpenSections(prev => ({ ...prev, face_enrollment: true }));
      setCameraModeActive(true);
      setFacePhotoError('');

      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 100);
    } catch (err) {
      setFacePhotoError('Unable to access camera: ' + err.message);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraModeActive(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      if (facePhotos.length >= 5) {
        setFacePhotoError('Maximum 5 photos allowed');
        return;
      }

      const context = canvasRef.current.getContext('2d');
      const video = videoRef.current;
      canvasRef.current.width = 400;
      canvasRef.current.height = 400;
      context.drawImage(video, 0, 0, 400, 400);
      const base64 = canvasRef.current.toDataURL('image/jpeg', 0.9);

      setFacePhotos((prev) => [...prev, base64]);
      setFacePhotoError('');
    }
  };

  const removePhoto = (index) => {
    setFacePhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFacePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (facePhotos.length >= 5) {
        setFacePhotoError('Maximum 5 photos allowed');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result;
        setFacePhotos((prev) => [...prev, base64]);
        setFacePhotoError('');
      };
      reader.readAsDataURL(file);
    }
  };

  const enrollFacePhotos = async () => {
    if (facePhotos.length === 0) {
      setFacePhotoError('Please add at least 1 face photo');
      return;
    }

    if (!form.employee_id?.trim()) {
      setFacePhotoError('Save employee first before enrolling face photos');
      return;
    }

    setEnrollingFace(true);
    try {
      await employeeService.enrollFace(form.employee_id, facePhotos);
      setFacePhotoError('');
      setFormSuccess('Successfully enrolled ' + facePhotos.length + ' face photos!');
      setTimeout(() => setFormSuccess(''), 3000);
    } catch (err) {
      setFacePhotoError(err.message || 'Failed to enroll face photos');
    } finally {
      setEnrollingFace(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    // Bug fix: some legacy records store fields (cnic, phone, bank number etc.) as
    // numbers instead of strings, which crashes .trim(). Force everything to string first.
    const textFields = [
      'employee_id', 'full_name', 'email', 'phone', 'cnic', 'father_name', 'address',
      'bank_name', 'bank_account_number', 'department', 'designation', 'personal_email',
      'emergency_contact_name', 'emergency_contact_phone', 'reporting_manager',
    ];
    for (const key of textFields) {
      if (form[key] != null && typeof form[key] !== 'string') {
        form[key] = String(form[key]);
      }
    }

    if (!form.full_name?.trim()) return setFormError('Full name is required.');
    if (!form.email?.trim()) return setFormError('Email is required.');
    if (!form.phone?.trim()) return setFormError('Phone number is required.');
    if (form.role === 'employee' && !form.department?.trim()) return setFormError('Department is required.');
    if (!form.designation?.trim()) return setFormError('Designation is required.');
    if (!form.father_name?.trim()) return setFormError("Father's name is required.");
    if (!form.address?.trim()) return setFormError('Address is required.');
    if (!form.bank_name?.trim()) return setFormError('Bank name is required.');
    if (!form.bank_account_number?.trim()) return setFormError('Bank account number is required.');
    if (!form.joining_date?.trim()) return setFormError('Joining date is required.');

    setSaving(true);

    try {
      const payload = {
        employee_id: form.employee_id.trim(),
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        father_name: form.father_name.trim(),
        address: form.address.trim(),
        bank_name: form.bank_name.trim(),
        bank_account_number: form.bank_account_number.trim(),
        department: form.department.trim(),
        designation: form.designation.trim(),
        joining_date: form.joining_date,
        status: form.status,
        employment_type: form.employment_type,
        role: form.role || 'employee',
      };

      if (form.cnic?.trim()) payload.cnic = form.cnic.trim();
      if (form.date_of_birth?.trim()) payload.date_of_birth = form.date_of_birth;
      if (form.personal_email?.trim()) payload.personal_email = form.personal_email.trim();
      if (form.emergency_contact_name?.trim()) payload.emergency_contact_name = form.emergency_contact_name.trim();
      if (form.emergency_contact_phone?.trim()) payload.emergency_contact_phone = form.emergency_contact_phone.trim();
      if (form.reporting_manager?.trim()) payload.reporting_manager = form.reporting_manager.trim();
      if (facePhotos.length > 0) payload.photo = facePhotos[0];

     if (isEditMode) {
        const updateRes = await employeeService.updateEmployee(employeeId, payload);
        if (updateRes.reactivation_email_sent) {
          setFormSuccess('Employee updated successfully. Re-activation email sent - they must set a new password before logging in.');
        } else {
          setFormSuccess('Employee updated successfully.');
        }

        // Save/update salary structure on edit too
        if (form.basic_salary > 0) {
          try {
            await api.post('/api/payroll/salary-structure', {
              employee_id: form.employee_id,
              basic_salary: form.basic_salary,
              allowances: {
                transport: form.transport_allowance || 0,
                housing: form.housing_allowance || 0,
                medical: form.medical_allowance || 0,
                other: form.other_allowance || 0
              },
              deductions: {
                tax: form.tax_deduction || 0,
                loan_repayment: form.loan_repayment || 0,
                other: form.other_deduction || 0
              }
            });
          } catch (salaryErr) {
            console.warn('Salary structure save warning:', salaryErr);
            setFormError('Employee updated, but salary save failed: ' + (salaryErr.message || 'Unknown error'));
          }
        }
      } else {
        const createdRes = await employeeService.createEmployee(payload);
        const createdEmployee = createdRes.data || createdRes;
        setFormSuccess('Employee created successfully.');

        // Save salary structure if basic salary is provided
        if (form.basic_salary > 0) {
          try {
            await api.post('/api/payroll/salary-structure', {
              employee_id: form.employee_id,
              basic_salary: form.basic_salary,
              allowances: {
                transport: form.transport_allowance || 0,
                housing: form.housing_allowance || 0,
                medical: form.medical_allowance || 0,
                other: form.other_allowance || 0
              },
              deductions: {
                tax: form.tax_deduction || 0,
                loan_repayment: form.loan_repayment || 0,
                other: form.other_deduction || 0
              }
            });
          } catch (salaryErr) {
            console.warn('Salary structure save warning:', salaryErr);
            setFormError('Employee created, but salary save failed: ' + (salaryErr.message || 'Unknown error'));
          }
        }

        // Auto-enroll face photos if available
        if (facePhotos.length > 0) {
          try {
            await employeeService.enrollFace(form.employee_id, facePhotos);
            setFormSuccess('Employee created and face photos enrolled!');
            setFacePhotos([]);
          } catch (faceErr) {
            console.warn('Face enrollment warning:', faceErr);
          }
        }

        setForm(EMPTY_FORM);
      }

      loadEmployees();
      setTimeout(() => {
        closeForm();
      }, 1500);
    } catch (err) {
      setFormError(err.message || 'Error saving employee.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (empId, fullName) => {
    if (!window.confirm('Delete employee ' + fullName + '? This cannot be undone.')) return;

    setDeletingId(empId);
    try {
      await employeeService.deleteEmployee(empId);
      loadEmployees();
    } catch (err) {
      setListError(err.message || 'Failed to delete employee');
    } finally {
      setDeletingId('');
    }
  };

  const openNewForm = () => {
  
    const today = new Date().toISOString().slice(0, 10);
    setForm({ ...EMPTY_FORM, employee_id: '', joining_date: today });
    setFacePhotos([]);
    setFacePhotoError('');
    setFormError('');
    setFormSuccess('');
    setCameraModeActive(false);
    setShowForm(true);
  };

  const closeForm = () => {
    stopCamera();
    setShowForm(false);
    setFormError('');
    setFormSuccess('');
    setCameraModeActive(false);
    setFacePhotos([]);
    setFacePhotoError('');
    if (isEditMode) navigate('/onboarding');
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadEmployees();
  };
if (!canAccess) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
        You do not have permission to access this page.
      </div>
    );
  }

  return (
    <div>
      {formSuccess && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-green-600 text-white px-5 py-3 rounded-control shadow-lg text-sm font-medium">
          <Check className="w-4 h-4" />
          {formSuccess}
        </div>
      )}
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Onboarding</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Create and manage employee profiles with face enrollment
          </p>
        </div>
        {!showForm && (
          <button
            onClick={openNewForm}
            className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark text-white px-4 py-2.5 rounded-control text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Employee
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-white rounded-card shadow-soft border border-gray-100 mb-8">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-800">
              {isEditMode ? 'Edit Employee' : 'New Employee'}
            </h2>
            <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          {loadingForm ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6">
              {formError && (
                <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-control bg-red-50 text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  {formError}
                </div>
              )}

              <Section id="auth" title="Authentication & ID" open={openSections.auth} onToggle={toggleSection}>
                <Field label="Employee ID" required>
                  <input
                    className={inputCls}
                    value={form.employee_id}
                    onChange={(e) => setField('employee_id', e.target.value)}
                    placeholder="Auto-generated"
                    disabled
                  />
                </Field>

                <div className="sm:col-span-2 px-4 py-3 rounded-control bg-blue-50 text-blue-700 text-sm">
                  <p className="text-xs font-medium">
                    {isEditMode
                      ? 'This employee sets their own password via the emailed setup link. There is no admin-set password.'
                      : 'A password setup email will be sent to ' + (form.email || 'the employee') + '. They will set their own password using that link.'}
                  </p>
                </div>
              </Section>

              <Section id="personal" title="Personal Information" open={openSections.personal} onToggle={toggleSection}>
                <Field label="Full Name" required>
                  <input
                    className={inputCls}
                    value={form.full_name}
                    onChange={(e) => setField('full_name', e.target.value)}
                    placeholder="John Doe"
                  />
                </Field>

                <Field label="Email" required>
                  <input
                    className={inputCls}
                    type="email"
                    value={form.email}
                    onChange={(e) => setField('email', e.target.value)}
                    placeholder="john@company.com"
                  />
                </Field>

                <Field label="Personal Email">
                  <input
                    className={inputCls}
                    type="email"
                    value={form.personal_email}
                    onChange={(e) => setField('personal_email', e.target.value)}
                    placeholder="john.doe@gmail.com"
                  />
                </Field>

                <Field label="Phone Number" required>
                  <input
                    className={inputCls}
                    type="tel"
                    value={form.phone}
                    onChange={(e) => handlePhoneInput(e.target.value)}
                    maxLength="18"
                    placeholder="+92 300 0000000"
                  />
                </Field>

                <Field label="CNIC">
                  <input
                    className={inputCls}
                    value={form.cnic}
                    onChange={(e) => handleCNICInput(e.target.value)}
                    maxLength="25"
                    placeholder="35202-1234567-1"
                  />
                </Field>

                <Field label="Date of Birth">
                  <input
                    className={inputCls}
                    type="date"
                    value={form.date_of_birth}
                    max="2099-12-31"
                    onChange={(e) => setField('date_of_birth', e.target.value)}
                  />
                </Field>

                <Field label="Emergency Contact Name">
                  <input
                    className={inputCls}
                    value={form.emergency_contact_name}
                    onChange={(e) => setField('emergency_contact_name', e.target.value)}
                    placeholder="Contact person name"
                  />
                </Field>

                <Field label="Emergency Contact Phone">
                  <input
                    className={inputCls}
                    type="tel"
                    value={form.emergency_contact_phone}
                    onChange={(e) => handleEmergencyPhoneInput(e.target.value)}
                    maxLength="18"
                    placeholder="+92 300 0000000"
                  />
                </Field>

                 <Field label="Father Name" required>
                  <input
                    className={inputCls}
                    value={form.father_name}
                    onChange={(e) => setField('father_name', e.target.value)}
                    placeholder="Father's full name"
                  />
                </Field>

                <Field label="Address" required>
                  <input
                    className={inputCls}
                    value={form.address}
                    onChange={(e) => setField('address', e.target.value)}
                    placeholder="House #, Street, City"
                  />
                </Field>

                <Field label="Bank Name" required>
                  <input
                    className={inputCls}
                    value={form.bank_name}
                    onChange={(e) => setField('bank_name', e.target.value)}
                    placeholder="e.g. HBL, UBL"
                  />
                </Field>

                <Field label="Bank Account Number" required>
                  <input
                    className={inputCls}
                    value={form.bank_account_number}
                    onChange={(e) => setField('bank_account_number', e.target.value)}
                    placeholder="0123456789012"
                  />
                </Field>

              </Section>

              <Section id="job" title="Job Information" open={openSections.job} onToggle={toggleSection}>
                <Field label="Department" required={form.role === 'employee'}>
                  {departments.length > 0 ? (
                    <select
                      className={inputCls}
                      value={form.department}
                      onChange={(e) => setField('department', e.target.value)}
                    >
                      <option value="">Select department</option>
                      {departments.map((d) => {
                        const deptName = d.name || d.department_name || d.dept_name || d.code;
                        const deptKey = d.id || d._id || d.code || d.name;
                        return (
                          <option key={deptKey} value={deptName}>
                            {deptName}
                          </option>
                        );
                      })}
                    </select>
                  ) : (
                    <input
                      className={inputCls}
                      value={form.department}
                      onChange={(e) => setField('department', e.target.value)}
                      placeholder="e.g. Engineering"
                    />
                  )}
                </Field>

                <Field label="Designation" required>
                  <input
                    className={inputCls}
                    value={form.designation}
                    onChange={(e) => setField('designation', e.target.value)}
                    placeholder="e.g. Software Engineer"
                  />
                </Field>

                <Field label="Employment Type">
                  <select
                    className={inputCls}
                    value={form.employment_type}
                    onChange={(e) => setField('employment_type', e.target.value)}
                  >
                    {EMPLOYMENT_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Role">
                  <select
                    className={inputCls}
                    value={form.role}
                    onChange={(e) => setField('role', e.target.value)}
                  >
                    {ROLES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Date of Joining" required>
                  <input
                    className={inputCls}
                    type="date"
                    value={form.joining_date}
                    max="2099-12-31"
                    onChange={(e) => setField('joining_date', e.target.value)}
                  />
                </Field>

                <Field label="Reporting Manager">
                  <input
                    className={inputCls}
                    value={form.reporting_manager}
                    onChange={(e) => setField('reporting_manager', e.target.value)}
                    placeholder="Manager name or ID"
                  />
                </Field>

                <Field label="Status">
                  <select
                    className={inputCls}
                    value={form.status}
                    onChange={(e) => setField('status', e.target.value)}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </Field>
              </Section>

              <Section
                id="salary"
                title="Salary Structure"
                open={openSections.salary}
                onToggle={toggleSection}
                badge={form.basic_salary > 0 ? <CheckCircle className="w-4 h-4 text-green-600" /> : null}
              >
                <Field label="Basic Salary" required>
                  <input
                    type="number"
                    className={inputCls}
                    value={form.basic_salary}
                    onChange={(e) => setForm({ ...form, basic_salary: parseFloat(e.target.value) || null })}
                    placeholder="0"
                  />
                </Field>

                <div className="sm:col-span-2">
                  <h4 className="font-semibold text-gray-700 mb-3 text-sm">Allowances</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Transport">
                      <input type="number" className={inputCls} value={form.transport_allowance} onChange={(e) => setForm({ ...form, transport_allowance: parseFloat(e.target.value) || null })} placeholder="0" />
                    </Field>
                    <Field label="Housing">
                      <input type="number" className={inputCls} value={form.housing_allowance} onChange={(e) => setForm({ ...form, housing_allowance: parseFloat(e.target.value) || null })} placeholder="0" />
                    </Field>
                    <Field label="Medical">
                      <input type="number" className={inputCls} value={form.medical_allowance} onChange={(e) => setForm({ ...form, medical_allowance: parseFloat(e.target.value) || null })} placeholder="0" />
                    </Field>
                    <Field label="Other">
                      <input type="number" className={inputCls} value={form.other_allowance} onChange={(e) => setForm({ ...form, other_allowance: parseFloat(e.target.value) || null })} placeholder="0" />
                    </Field>
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <h4 className="font-semibold text-gray-700 mb-3 text-sm">Deductions</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Tax">
                      <input type="number" className={inputCls} value={form.tax_deduction} onChange={(e) => setForm({ ...form, tax_deduction: parseFloat(e.target.value) || 0 })} placeholder="0" />
                    </Field>
                    <Field label="Loan Repayment">
                      <input type="number" className={inputCls} value={form.loan_repayment} onChange={(e) => setForm({ ...form, loan_repayment: parseFloat(e.target.value) || 0 })} placeholder="0" />
                    </Field>
                    <Field label="Other">
                      <input type="number" className={inputCls} value={form.other_deduction} onChange={(e) => setForm({ ...form, other_deduction: parseFloat(e.target.value) || 0 })} placeholder="0" />
                    </Field>
                  </div>
                </div>
              </Section>

              <Section
                id="face_enrollment"
                title="Face Enrollment for Attendance"
                open={openSections.face_enrollment}
                onToggle={toggleSection}
                badge={
                  facePhotos.length > 0 && (
                    <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">
                      {facePhotos.length}/5
                    </span>
                  )
                }
              >
                <div className="sm:col-span-2">
                  {facePhotoError && (
                    <div className="mb-3 px-3 py-2 rounded-control bg-red-50 text-red-700 text-xs">
                      {facePhotoError}
                    </div>
                  )}

                  {cameraModeActive ? (
                    <div className="space-y-3">
                      <div className="flex justify-center mb-2">
                        <div className="w-80 h-80 rounded-2xl overflow-hidden bg-black border-4 border-primary shadow-lg">
                          <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                      <canvas ref={canvasRef} className="hidden" />

                      <div className="text-center mb-3">
                        <p className="text-xs text-gray-500">Position face in center. Good lighting required.</p>
                      </div>
                      <div className="flex gap-2 justify-center">
                        <button
                          type="button"
                          onClick={capturePhoto}
                          disabled={facePhotos.length >= 5}
                          className="flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark text-white px-6 py-2.5 rounded-control text-sm font-medium transition-colors disabled:opacity-60"
                        >
                          <Camera className="w-4 h-4" />
                          Capture ({facePhotos.length}/5)
                        </button>
                        <button
                          type="button"
                          onClick={stopCamera}
                          className="px-6 py-2.5 rounded-control border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                          Stop
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {facePhotos.length > 0 && (
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                          {facePhotos.map((photo, idx) => (
                            <div key={idx} className="relative rounded-lg overflow-hidden aspect-square bg-gray-100 border border-gray-200">
                              <img src={photo} alt={'Face ' + (idx + 1)} className="w-full h-full object-cover" />
                              <button
                                type="button"
                                onClick={() => removePhoto(idx)}
                                className="absolute top-0.5 right-0.5 bg-red-500 hover:bg-red-600 text-white p-0.5 rounded-full"
                              >
                                <X className="w-3 h-3" />
                              </button>
                              <div className="absolute bottom-0.5 left-0.5 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                                {idx + 1}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex gap-2 justify-center">
                        <button
                          type="button"
                          onClick={startCamera}
                          disabled={facePhotos.length >= 5}
                          className="flex-1 flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark text-white px-4 py-2.5 rounded-control text-sm font-medium transition-colors disabled:opacity-60"
                        >
                          <Camera className="w-4 h-4" />
                          Take Photo
                        </button>
                        <label className="flex-1">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFacePhotoUpload}
                            disabled={facePhotos.length >= 5}
                            className="hidden"
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.currentTarget.parentElement?.querySelector('input')?.click();
                            }}
                            disabled={facePhotos.length >= 5}
                            className="w-full flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2.5 rounded-control text-sm font-medium transition-colors disabled:opacity-60"
                          >
                            <Upload className="w-4 h-4" />
                            Upload
                          </button>
                        </label>
                      </div>

                      {facePhotos.length > 0 && (
                        <button
                          type="button"
                          onClick={enrollFacePhotos}
                          disabled={enrollingFace}
                          className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-control text-sm font-medium transition-colors disabled:opacity-60"
                        >
                          {enrollingFace ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Enrolling...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              Enroll {facePhotos.length} Photo{facePhotos.length !== 1 ? 's' : ''}
                            </>
                          )}
                        </button>
                      )}

                      <p className="text-xs text-gray-500 text-center">
                        Capture 5 clear photos from different angles for best results
                      </p>
                    </div>
                  )}
                </div>
              </Section>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-5 py-2.5 rounded-control border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 bg-primary hover:bg-primary-dark text-white px-6 py-2.5 rounded-control text-sm font-medium transition-colors disabled:opacity-60"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isEditMode ? 'Update Employee' : 'Create Employee'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {!showForm && (
        <div>
          <form onSubmit={handleSearchSubmit} className="mb-4 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or employee ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2.5 rounded-control border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Search
            </button>
          </form>

          {listError && (
            <div className="mb-4 px-4 py-2.5 rounded-control bg-red-50 text-red-700 text-sm">
              {listError}
            </div>
          )}

          {listLoading && (
            <div className="flex items-center justify-center py-20 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}

          {!listLoading && employees.length === 0 && (
            <div className="bg-white rounded-card shadow-soft border border-gray-100 p-12 text-center">
              <UserCheck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No employees yet. Click "New Employee" to start.</p>
            </div>
          )}

          {!listLoading && employees.length > 0 && (
            <>
              <div className="hidden md:block bg-white rounded-card shadow-soft border border-gray-100 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left text-xs font-medium text-gray-400 uppercase tracking-wide">
                      <th className="px-5 py-3">Employee ID</th>
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3">Email</th>
                      <th className="px-5 py-3">Department</th>
                      <th className="px-5 py-3">Designation</th>
                      <th className="px-5 py-3">Role</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((e) => (
                      <tr key={e.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors">
                        <td className="px-5 py-3.5 text-gray-500 text-xs font-medium">{e.employee_id}</td>
                        <td className="px-5 py-3.5 font-medium text-gray-800">{e.full_name}</td>
                        <td className="px-5 py-3.5 text-gray-600">{e.email}</td>
                        <td className="px-5 py-3.5 text-gray-600">{e.department}</td>
                        <td className="px-5 py-3.5 text-gray-600">{e.designation}</td>
                        <td className="px-5 py-3.5 text-gray-600 capitalize">
                        {(e.role || '').replace(/_/g, ' ')}
                        {e.role !== 'employee' && e.account_active === false && (
                        <span className="ml-2 bg-yellow-100 text-yellow-700 text-xs px-2 py-0.5 rounded-full">Pending</span>  )}</td>
                        <td className="px-5 py-3.5"><StatusBadge status={e.status} /></td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center justify-end gap-3">
                            <button
                              onClick={() => navigate('/onboarding/' + e.employee_id)}
                              className="text-gray-400 hover:text-primary transition-colors"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(e.employee_id, e.full_name)}
                              disabled={deletingId === e.employee_id}
                              className="text-gray-400 hover:text-red-500 disabled:opacity-50 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden space-y-3">
                {employees.map((e) => (
                  <div key={e.id} className="bg-white rounded-card shadow-soft border border-gray-100 p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <p className="font-medium text-gray-800 text-sm">{e.full_name}</p>
                        <p className="text-xs text-gray-400 mt-1">{e.employee_id}</p>
                      </div>
                      <StatusBadge status={e.status} />
                    </div>
                    <div className="space-y-1 mb-3">
                      <p className="text-xs text-gray-600">{e.email}</p>
                      <p className="text-xs text-gray-600">{e.department} + {e.designation}</p>
                      <p className="text-xs text-gray-500 capitalize">Role: {(e.role || '').replace(/_/g, ' ')}</p>
                    </div>
                    <div className="flex items-center gap-4 pt-3 border-t border-gray-50">
                      <button
                        onClick={() => navigate('/onboarding/' + e.employee_id)}
                        className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary-dark transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(e.employee_id, e.full_name)}
                        disabled={deletingId === e.employee_id}
                        className="flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-700 disabled:opacity-50 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

    </div>
  );
};

export default Onboarding;